import maUploadDocument from '@/modules/Document/views/handleDocumentContainer/components/documentUploadStep'
import maActionsButtons from '@/components/button-blue'
import maModal from '@/components/modal'
import maSpinner from '@/components/spinner'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import {
  billmaApiPostFile,
  billmaApiGetFile
} from '@/modules/Document/services/file'
import { billmaApiGetDocuments } from '@/modules/Document/services/document'
import { billmaApiPutClaim } from '@/modules/Claim/services/sinister'
import { mapMutations } from 'vuex'
import { UPDATE_LOADER } from '@/store/types'
import downloadFileFromFetchRequest from '@/utils/downloadFileFromFetchRequest'
// import { formatAmount } from '@/utils/amounts'
import { BILLMA_DOCUMENT_ICR_STATUS, BILLMA_DOCUMENTS_TYPES } from '@/constants'
import { FLOW_TRANSITIONS } from '@/modules/Flow/constants'

const mapDocuments = (documentsApiData) => {
  return documentsApiData.map((document) => {
    return {
      id: document.file.createdName,
      name: document.file.name,
      canBeDeleted: !document.processStatus
    }
  })
}

const icrTextChangeTimeout = 4000

export default {
  name: 'handleDocumentContainer',

  mixins: [flowMixin],

  data() {
    return {
      uploadLoading: false,
      icrLoading: false,
      icrProcessingModalTextActive: 'one',
      handleDocumentStepActive: 'uploadDocuments',
      processedClaimDocuments: [],
      toProcessClaimDocuments: [],
      claimId: null
    }
  },

  computed: {
    primaryButtonIsDisabled() {
      return this.mappedDocuments.length === 0 || this.icrLoading
    },
    mappedDocuments() {
      const mappedClaimDocuments = mapDocuments([
        ...this.processedClaimDocuments,
        ...this.toProcessClaimDocuments
      ])

      return mappedClaimDocuments
    },
    icrIsProcessing() {
      return this.processedClaimDocuments.some(
        (document) =>
          document.processStatus === BILLMA_DOCUMENT_ICR_STATUS.PROCESSING ||
          document.processStatus === BILLMA_DOCUMENT_ICR_STATUS.PENDING
      )
    }
  },

  components: {
    maUploadDocument,
    maActionsButtons,
    maModal,
    maSpinner
  },

  methods: {
    ...mapMutations({
      updateLoader: UPDATE_LOADER
    }),

    redirectToErrorView() {
      this.$router.replace({ name: 'error' }).catch((e) => {})
    },

    async uploadFile(file) {
      try {
        const fileData = await billmaApiPostFile({
          fileBlob: file,
          sinisterId: this.getSinisterId,
          documentType: BILLMA_DOCUMENTS_TYPES.BUDGET // In this version, BUDGET is the value by default.
        })

        this.toProcessClaimDocuments.push({ file: { ...fileData } })

        return fileData
      } catch {
        this.redirectToErrorView()
      }
    },

    async setClaimDocuments() {
      try {
        const { claimDtoList } = await billmaApiGetDocuments(this.getSinisterId)
        if (claimDtoList.length > 0) {
          this.claimId = claimDtoList[0].claimId
          this.processedClaimDocuments = claimDtoList
        }
      } catch {
        this.redirectToErrorView()
      }
    },

    async triggerIcr() {
      try {
        const claimExistsInBillma = this.claimId
        // If claim/siniste doesn't exist in Billma, we have to set the sinisterID to 0 because of api requirement
        await billmaApiPutClaim({
          sinisterId: claimExistsInBillma ? this.claimId : 0,
          externalId: this.getSinisterId,
          documents: this.toProcessClaimDocuments
        })
      } catch {
        this.redirectToErrorView()
      }
    },

    async checkIfIcrIsprocessing(promiseResolve) {
      await this.setClaimDocuments()
      if (this.icrIsProcessing) {
        setTimeout(() => {
          this.checkIfIcrIsprocessing(promiseResolve)
        }, 2000)
      } else {
        promiseResolve()
      }
    },

    handleIcrProcessing() {
      return new Promise((resolve) => {
        this.checkIfIcrIsprocessing(resolve)
      })
    },

    handleIcrResult() {
      const documentsAreValid = this.processedClaimDocuments.every(
        (document) =>
          document.processStatus === BILLMA_DOCUMENT_ICR_STATUS.VALID
      )
      if (documentsAreValid) {
        // let compensationAmount = 0
        // this.processedClaimDocuments.forEach((document) => {
        //   compensationAmount += document.amountWithTaxes
        // })
        // formatAmount(compensationAmount)
        // this.goToNextState(FLOW_TRANSITIONS.FINISHED_OK, {
        //   amount: formatAmount(compensationAmount)
        // })
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_OK)
      } else {
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_KO)
      }
    },

    handleIcrProcessingTextChange() {
      if (!this.icrLoading) return
      this.icrProcessingModalTextActive === 'one'
        ? (this.icrProcessingModalTextActive = 'two')
        : (this.icrProcessingModalTextActive = 'one')
      setTimeout(() => {
        this.handleIcrProcessingTextChange()
      }, icrTextChangeTimeout)
    },

    async handleIcrOnMounted() {
      if (this.icrIsProcessing) {
        this.icrLoading = true
        this.$refs.icrProcessingModal.showModal = true
        setTimeout(() => {
          this.handleIcrProcessingTextChange()
        }, icrTextChangeTimeout)
        await this.handleIcrProcessing()
        this.icrLoading = false
        this.$refs.icrProcessingModal.showModal = false
        this.handleIcrResult()
      }
    },

    async handleDocumentInputChange(file) {
      this.uploadLoading = true
      await this.uploadFile(file)
      this.uploadLoading = false
    },

    async handlePrimaryButtonClick() {
      this.icrLoading = true
      this.$refs.icrProcessingModal.showModal = true
      setTimeout(() => {
        this.handleIcrProcessingTextChange()
      }, icrTextChangeTimeout)

      try {
        await this.triggerIcr()
        this.toProcessClaimDocuments = []
        await this.handleIcrProcessing()
        this.icrLoading = false
        this.$refs.icrProcessingModal.showModal = false
        this.handleIcrResult()
      } catch {
        this.goToNextState(FLOW_TRANSITIONS.FINISHED_ERROR)
      }
    },

    handleSecondaryButtonClick() {
      this.goToNextState(FLOW_TRANSITIONS.SHOW_COMPENSATION_TYPE)
    },

    handleDocumentDeleteClick(filename) {
      this.toProcessClaimDocuments = this.toProcessClaimDocuments.filter(
        (document) => document.file.createdName !== filename
      )
    },

    async handleDocumentDownloadClick({ documentId, documentName }) {
      try {
        const fileData = await billmaApiGetFile(documentId)
        downloadFileFromFetchRequest(fileData, documentName)
      } catch {
        this.redirectToErrorView()
      }
    }
  },

  async mounted() {
    this.updateLoader(true)
    await this.setClaimDocuments()
    await this.handleIcrOnMounted()
    this.updateLoader(false)
  }
}
